import React, {useEffect, useState} from 'react';
import {Modal} from 'react-bootstrap';
import {useNavigate} from 'react-router-dom';
import {usePendingOrders} from '../../../product/application/usePendingOrders';
import {AddRestockedToCartButton} from '../../../product/ui/AddRestockedToCartButton';
import {PopupStorageService} from '../../../notification/services/PopupStorageService';
import {PriceWithFallback} from '../../../common/ui/PriceWithFallback';
import {useComercialContext} from '../../../comercial/ComercialContext';
import {isSuccess} from '../../../common/domain/RemoteData.model';

const PendingOrderRow = ({pendingOrder, onGoToProduct, children}) => {
    const {product, pendingQuantity} = pendingOrder;
    return (
        <li className="list-group-item">
            <div className="d-flex flex-column flex-md-row align-items-md-center gap-3">
                <img src={product.imageUrl}
                    alt={product.name}
                    style={{width: '64px', height: '64px', objectFit: 'contain', cursor: 'pointer'}}
                    onClick={() => onGoToProduct(product.code)}
                />
                <div className="flex-fill">
                    <span role="button"
                        className="fw-semibold"
                        onClick={() => onGoToProduct(product.code)}>
                        {product.name}
                    </span>
                    <br/>
                    <small className="text-muted">
                        REF: {product.code} · {pendingQuantity} ud. pendientes
                    </small>
                    <div>
                        <PriceWithFallback data={{
                            sellingPrice: product.price?.bestPrice,
                            basePrice: product.price?.value
                        }}/>
                    </div>
                </div>
                {children && <div style={{minWidth: '200px'}}>{children}</div>}
            </div>
        </li>
    );
};

/**
 * Popup que se muestra una vez por sesión al entrar en el portal con los
 * pedidos pendientes del cliente, separados en los que ya tienen stock (se
 * pueden añadir al carrito) y los que siguen en espera (informativo).
 */
export const RestockedPendingPopup = () => {
    const {pendingData, allPending, restocked, waiting, refresh, removeLocally} = usePendingOrders();
    const {actingAsCliente} = useComercialContext();
    const [show, setShow] = useState(false);
    const navigate = useNavigate();

    const popupKey = `pendientes_${actingAsCliente?.numCliente ?? 'self'}`;

    useEffect(() => {
        if (!isSuccess(pendingData) || PopupStorageService.hasBeenSeen(popupKey)) {
            return;
        }
        if (allPending.length > 0) {
            PopupStorageService.markAsSeen(popupKey);
            setShow(true);
        }
        // Solo debe evaluarse cuando termina la carga de pendientes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingData, popupKey]);

    const handleClose = () => setShow(false);

    const handleAdded = (code) => {
        removeLocally(code);
        if (allPending.length <= 1) {
            setShow(false);
        }
    };

    const handleGoToProduct = (code) => {
        setShow(false);
        navigate(`/articulos/${encodeURIComponent(code)}`);
    };

    if (!show || allPending.length === 0) {
        return null;
    }

    return (
        <Modal show={show} onHide={handleClose} centered size="lg">
            <Modal.Header closeButton>
                <Modal.Title className="h5">Tus pedidos pendientes</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {restocked.length > 0 && (
                    <div className="mb-4">
                        <h3 className="h6">Ya tienes stock</h3>
                        <p className="text-muted">
                            Hemos repuesto estos productos que tenías pendientes. Ya puedes
                            añadirlos a tu carrito:
                        </p>
                        <ul className="list-group">
                            {restocked.map((pendingOrder) => (
                                <PendingOrderRow key={pendingOrder.product.code}
                                    pendingOrder={pendingOrder}
                                    onGoToProduct={handleGoToProduct}>
                                    <AddRestockedToCartButton
                                        pendingOrder={pendingOrder}
                                        onAdded={handleAdded}
                                        onFailed={refresh}
                                    />
                                </PendingOrderRow>
                            ))}
                        </ul>
                    </div>
                )}
                {waiting.length > 0 && (
                    <div>
                        <h3 className="h6">En espera</h3>
                        <p className="text-muted">
                            Todavía no tenemos stock de estos productos. Te avisaremos aquí en
                            cuanto los repongamos:
                        </p>
                        <ul className="list-group">
                            {waiting.map((pendingOrder) => (
                                <PendingOrderRow key={pendingOrder.product.code}
                                    pendingOrder={pendingOrder}
                                    onGoToProduct={handleGoToProduct}
                                />
                            ))}
                        </ul>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                    Cerrar
                </button>
            </Modal.Footer>
        </Modal>
    );
};
